import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageSquare, ArrowRight, Brain, Cpu, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FEATURES_IMAGE = '/assets/features-preview.jpg';

const FeaturesPreview = () => {
  const features = [
    {
      icon: Brain,
      title: 'AI Product Recommendations',
      description: 'Neural networks analyze customer behavior to suggest perfect products in real-time.',
      color: 'bg-teal-50 border-teal-200',
      iconBg: 'bg-teal-500'
    },
    {
      icon: MessageSquare,
      title: 'Conversational AI',
      description: 'Natural language processing enables human-like shopping conversations.',
      color: 'bg-cyan-50 border-cyan-200',
      iconBg: 'bg-cyan-500'
    },
    {
      icon: Palette,
      title: 'Customize Appearance',
      description: 'Match your chatbot design with your brand identity and messaging style.',
      color: 'bg-pink-50 border-pink-200',
      iconBg: 'bg-pink-500'
    }
  ];

  return (
    <section id="features" className="py-16 sm:py-24 md:py-32 bg-slate-50" data-testid="features-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-teal-100 border border-teal-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
            <Cpu className="w-4 h-4 text-teal-600" />
            <span className="text-xs sm:text-sm font-bold text-teal-700">AI Capabilities</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-5">
            More Than a Chatbot.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">An AI Sales Agent.</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            Transform every visitor interaction into revenue with intelligent AI automation.
          </p>
        </motion.div>

        {/* Two-column: Image + Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-8 sm:mb-12 items-center">
          {/* Image Side */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={FEATURES_IMAGE}
              alt="AI chatbot interface powering e-commerce"
              className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[280px] sm:h-[380px] lg:h-[440px] object-cover border-2 border-slate-200"
              data-testid="features-preview-image"
            />
            <motion.div 
              className="absolute -bottom-4 -right-3 sm:-right-5 bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-teal-100"
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Brain className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">AI Engaged</p>
                  <p className="text-xs text-emerald-600 font-medium">+42% sales</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Features Side */}
          <div className="space-y-4 sm:space-y-5">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className={`bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 border-2 ${feature.color} shadow-sm hover:shadow-xl transition-all duration-300 group`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ x: 5 }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 sm:w-14 h-12 sm:h-14 rounded-xl sm:rounded-2xl ${feature.iconBg} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <feature.icon className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Link to="/features">
            <Button 
              size="lg" 
              variant="outline" 
              className="group border-2 border-slate-300 text-slate-700 hover:text-teal-600 hover:border-teal-500 hover:bg-teal-50 font-semibold text-sm sm:text-base px-6 sm:px-8"
              data-testid="view-all-features-btn"
            >
              Explore All AI Features
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesPreview;
